import { BottomSheet } from '@s4l/ds'
import { useS4LContext } from '@s4l/react-context'
import type { ListOfAncillary } from '@s4l/stayforlong/booking/ListOfAncillary'
import type { ListOfBanner } from '@s4l/stayforlong/marketing/ListOfBanner'
import { Advantages, Banner, SubscriptionBox, TrustPilotWidget } from '@s4l/uikit'
import { rtlLeftNormalizer } from '@s4l/utils/library/rtl'
import cn from 'classnames'
import { FacilitiesV2 } from 'client/components/FacilitiesV2'
import Footer from 'client/components/Footer'
import Header from 'client/components/Header'
import { THEME_TERTIARY } from 'client/components/Header/constants'
import Rating from 'client/components/Rating'
import { fetchLocationSuccessAction } from 'client/contexts/Location/actions'
import { getLocation } from 'client/contexts/Location/selectors'
import { getShowLoginModal } from 'client/contexts/Login/selectors'
import { fetchPropertySuccessAction } from 'client/contexts/Property/actions'
import { sendTrackingRequestAction } from 'client/contexts/Tracking/actions'
import { kafkaDirectEvent } from 'client/contexts/Tracking/events'
import { updatedBreakpoint } from 'client/styles'
import { getSearchParamsFromQuery, sanitizeSearchParams } from 'client/utils'
import { getSearchParamsLocalStorage, setSearchParamsLocalStorage } from 'lib/storage'
import { buildLocation, scrollToPosition } from 'lib/utils'
import { useRouter } from 'next/router'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useIntersection, useMedia } from 'react-use'
import Button from 'src/comps/Atom/Buttons/PrimaryButton'
import BackToResults from 'src/comps/BackToResults'
import BuilderModal from 'src/comps/BuilderModal'
import Description from 'src/comps/Description'
import Gallery from 'src/comps/Gallery'
import Conditions from 'src/comps/Molecules/Conditions'
import MoreAccommodations from 'src/comps/Molecules/MoreAccommodations'
import NavigationBar from 'src/comps/NavigationBar'
import Tab from 'src/comps/NavigationBar/Tab'
import { WidgetPlaceholder } from 'src/comps/WidgetPlaceholder'
import { DEFAULT_SEARCH } from 'src/constants/search'
import { SUBSCRIPTION_BOX_HOTEL_PAGE } from 'src/constants/subscriptionSources'
import { getIsFetching } from 'src/effects/Availability/selectors'
import { useFetchReviews } from 'src/effects/Reviews/hooks'
import { updateSearchAction } from 'src/effects/Search/actions'
import { getSearchPaxes } from 'src/effects/Search/selectors'
import { useDevice } from 'src/hooks/useDevice'
import { useKafkaEvent } from 'src/hooks/useKafkaEvent'
import { SECTIONS } from 'src/hooks/useKafkaEvent/constants'
import { useLazyLoading } from 'src/hooks/useLazyLoading'
import { useMarket } from 'src/hooks/useMarket'
import { usePage } from 'src/hooks/usePage'
import useScrollTo from 'src/hooks/useScrollTo'
import useStorageAffiliateProperty from 'src/hooks/useStorageAffiliateProperty'
import Head from 'src/pages/hotel/[country]/[slug]/Head'
import { BreakpointSizes } from 'src/types/device'
import type { PropertyConditions, PropertyAPIResponse as PropertyType } from 'src/types/property'
import HotelDetailMainPrice from '../MainPrice'
import { getActiveSection } from '../utils'
import Section from './Section'
import Separator from './Separator'
import RoomSection from './rooms-section'
import styles from './styles.module.css'
import HotelDetailTitle from './title'
import useBus from 'src/hooks/useBus'

type queryType = {
  adults: string
  checkin: string
  checkout: string
  checkIn: string
  checkOut: string
  children: string
  hotelId: string
  market: string
  page: string
  relativeInDate: string
  relativeOutDate: string
  // eslint-disable-next-line camelcase
  internal_navigation: string
}

export enum ContentSections {
  OVERVIEW = 'overview',
  FACILITIES = 'facilities',
  DETAILS = 'details',
  REVIEWS = 'reviews',
  CTA = 'cta',
  ROOMS = 'rooms',
}

export interface IContentProps {
  readonly property: PropertyType
  readonly location: {
    country: string
    type: string
    slug: string
    city: string
    purchaseId: string
  }
  readonly ancillariesJSON: ReturnType<ListOfAncillary['toJSON']>
  readonly bannersJSON?: ReturnType<ListOfBanner['toJSON']>
}

export default function Content({
  property,
  location,
  ancillariesJSON,
  bannersJSON,
}: Readonly<IContentProps>) {
  const intl = useIntl()
  const dispatch = useDispatch()
  const { domain } = useS4LContext()

  const { isBot, activeBreakpoint, device } = useDevice()
  const { market, language, locale } = useMarket()
  const [searchDataUpdated, setSearchDataUpdated] = useState(false)
  const { source, setAffiliateProperty } = useStorageAffiliateProperty(property?.id)

  const [sectionsActive, setSectionsActive] = useState({
    isOverViewTabSelected: false,
    isRoomsTabSelected: false,
    isFacilitiesTabSelected: false,
    isDetailsTabSelected: false,
    isReviewsTabSelected: false,
  })

  const router = useRouter()
  const { pageId } = usePage()

  const { query } = router
  const { aid } = query

  const queryParams = getSearchParamsFromQuery({ ...query, market } as queryType)
  const isMobile = activeBreakpoint < BreakpointSizes.M
  const isWide = useMedia(updatedBreakpoint.tablet, device === 'desktop')

  const { sendEvent: sendKafkaClick } = useKafkaEvent({ page: 'hotel_details' })

  const occupancies = useSelector(getSearchPaxes)
  const locationState = useSelector(getLocation)
  const isFetchingRooms = useSelector(getIsFetching)
  const isLoginOpened = useSelector(getShowLoginModal)

  const roomsRef = useRef(null)
  const hotelOverviewRef = useRef<HTMLDivElement>(null)
  const hotelFacilitiesRef = useRef<HTMLDivElement>(null)
  const hotelDetailsRef = useRef<HTMLDivElement>(null)
  const reviewsRef = useRef<HTMLDivElement>(null)

  const [isGalleryVisible, setIsGalleryVisible] = useState(false)
  const [isRoomGalleryVisible, setIsRoomGalleryVisible] = useState(false)

  const { subscribe } = useBus()

  subscribe('show-room-gallery', () => {
    setIsRoomGalleryVisible(true)
  })
  subscribe('hide-room-gallery', () => {
    setIsRoomGalleryVisible(false)
  })

  const highlightedPropertyQueryParam = {
    highlightedProperty: property?.id.toString(),
  }

  const resultsURL = buildLocation({
    page: 'list',
    path: location,
    query: { ...highlightedPropertyQueryParam },
  })

  useEffect(() => {
    try {
      const paramsLocal = getSearchParamsLocalStorage()
      const searchParams = sanitizeSearchParams({
        ...DEFAULT_SEARCH,
        ...paramsLocal,
        ...queryParams,
        term: property.searchTerm,
        city: property.city,
        country: property.country,
        slug: property.citySlug || '',
        type: 'city',
        market,
      })

      dispatch(updateSearchAction(searchParams))

      setSearchParamsLocalStorage(searchParams)
    } catch (error) {
      console.warn(`[HDP] setLocalstorage: ${(error as Error)?.message}`)
    } finally {
      setSearchDataUpdated(true)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (property) {
      dispatch(fetchLocationSuccessAction(property))
      dispatch(fetchPropertySuccessAction(property))
      domain.AddByIDLastSeenPropertyUseCase.execute({ id: property.id })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, property])

  useFetchReviews({
    language,
    propertyId: !isBot ? property?.id : undefined,
    ref: reviewsRef,
  })

  useEffect(() => {
    if (property?.id && aid) {
      const sourceFromQuery = query.source
      setAffiliateProperty(aid, sourceFromQuery)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (property?.id && !isFetchingRooms) {
      dispatch(
        sendTrackingRequestAction({
          event: kafkaDirectEvent({
            eventName: 'ViewContent',
            ...location,
          }),
        })
      )
    }
  }, [property?.id, isFetchingRooms]) // eslint-disable-line react-hooks/exhaustive-deps

  const { scroll: scrollOverview } = useScrollTo({
    ref: hotelOverviewRef,
    margin: !isMobile ? 0 : -60,
  })
  const { scroll: scrollFacilities } = useScrollTo({
    ref: hotelFacilitiesRef,
    margin: !isMobile ? -60 : -35,
  })
  const { scroll: scrollDetails } = useScrollTo({
    ref: hotelDetailsRef,
    margin: !isMobile ? -60 : 0,
  })
  const { scroll: scrollRatings } = useScrollTo({ ref: reviewsRef, margin: !isMobile ? -80 : -35 })
  const { scroll: scrollRooms } = useScrollTo({ ref: roomsRef, margin: !isMobile ? 0 : -60 })

  const hotelHoverViewInt = useIntersection(hotelOverviewRef, {
    root: null,
    rootMargin: '0px',
    threshold: 0.15,
  })

  const facilitiesInt = useIntersection(hotelFacilitiesRef, {
    root: null,
    rootMargin: '0px',
    threshold: 0.15,
  })

  const hotelDetailsInt = useIntersection(hotelDetailsRef, {
    root: null,
    rootMargin: '0px',
    threshold: 0.15,
  })

  const reviewsInt = useIntersection(reviewsRef, {
    root: null,
    rootMargin: '0px',
    threshold: 0.5,
  })

  const roomsIntersection = useIntersection(roomsRef, {
    root: null,
    rootMargin: '0px',
    threshold: 0.1,
  })

  const onClickBottomSheetButton = () => {
    scrollRooms()
    sendKafkaClick({ section: 'cta_choose_room_step' })
  }

  function handleScroll() {
    scrollToPosition({ ref: roomsRef, margin: !isMobile ? 0 : -60 })
  }

  const scrollToRooms = useCallback(() => {
    scrollRooms()
  }, [scrollRooms])

  const scrollToRatings = useCallback(() => {
    scrollRatings()
    sendKafkaClick(
      { section: 'hotel_ratings' },
      {
        occupancies,
        search: {
          country: locationState?.country,
          type: locationState?.citySlug ? 'city' : 'zone',
          slug: locationState?.slug,
        },
      }
    )
  }, [scrollRatings, sendKafkaClick, occupancies, locationState])

  const handleTabClick = (tabReference: ContentSections) => {
    sendKafkaClick({ section: `navigation_${tabReference}` })

    const scrollers = {
      [ContentSections.OVERVIEW]: scrollOverview,
      [ContentSections.ROOMS]: scrollToRooms,
      [ContentSections.CTA]: scrollToRooms,
      [ContentSections.FACILITIES]: scrollFacilities,
      [ContentSections.DETAILS]: scrollDetails,
      [ContentSections.REVIEWS]: scrollRatings,
    }

    scrollers[tabReference]?.()
  }

  useEffect(() => {
    setSectionsActive(
      getActiveSection({
        isOverViewInt: hotelHoverViewInt,
        isFacilitiesInt: facilitiesInt,
        isDetailsInt: hotelDetailsInt,
        isReviewsInt: reviewsInt,
        isRoomsInt: roomsIntersection,
      })
    )
  }, [hotelHoverViewInt, facilitiesInt, hotelDetailsInt, reviewsInt, roomsIntersection])

  useEffect(() => {
    requestAnimationFrame(() => {
      if (!isGalleryVisible && !isRoomGalleryVisible) {
        document.body.style.pointerEvents = 'auto' // Open bug: https://github.com/emilkowalski/vaul/issues/492
      }
    })
  }, [sectionsActive, isGalleryVisible, isRoomGalleryVisible])

  const loadedReviews = useLazyLoading(reviewsRef)

  // We don't care about the domain, we are just adding it so we can use
  // the pathname attribute from URL to strip unwanted query params.
  // This is important because the `Head` will generate all meta attributes
  // for the open graph spec.
  const localUrl = new URL(router.asPath, 'https://stayforlong.com')

  const overviewTabRef = useRef<HTMLLIElement>(null)
  const roomsTabRef = useRef<HTMLLIElement>(null)
  const facilitiesTabRef = useRef<HTMLLIElement>(null)
  const detailsTabRef = useRef<HTMLLIElement>(null)
  const reviewsTabRef = useRef<HTMLLIElement>(null)

  const refs = useMemo(
    () => ({
      [ContentSections.OVERVIEW]: overviewTabRef,
      [ContentSections.ROOMS]: roomsTabRef,
      [ContentSections.CTA]: roomsTabRef,
      [ContentSections.FACILITIES]: facilitiesTabRef,
      [ContentSections.DETAILS]: detailsTabRef,
      [ContentSections.REVIEWS]: reviewsTabRef,
    }),
    []
  )

  const updateTabScroll = useCallback(
    (elementKey: ContentSections) => {
      const tab = refs[elementKey].current

      if (!tab) return

      const container = tab.parentElement
      const scrollLeft = rtlLeftNormalizer(tab.offsetLeft - 32)

      container?.scrollTo?.({
        left: scrollLeft,
        behavior: 'smooth',
      })
    },
    [refs]
  )

  function onTabClick(section: ContentSections) {
    updateTabScroll(section)
    handleTabClick(section)
  }

  useEffect(() => {
    const selected = Object.entries(sectionsActive).find(([, value]) => value)
    const scrollers = {
      isOverViewTabSelected: ContentSections.OVERVIEW,
      isRoomsTabSelected: ContentSections.ROOMS,
      isFacilitiesTabSelected: ContentSections.FACILITIES,
      isDetailsTabSelected: ContentSections.DETAILS,
      isReviewsTabSelected: ContentSections.REVIEWS,
    }

    if (!selected?.[0]) return

    const key = selected[0] as keyof typeof scrollers
    const scroller = scrollers[key]

    if (scroller) updateTabScroll(scroller)
  }, [sectionsActive, updateTabScroll])

  const isConditionsSectionAvailable = () => {
    if (!property.conditions) return false
    const { checkin, checkout, fees, policies, comments } =
      property.conditions as PropertyConditions

    const hasValidCheckInCheckOut =
      checkin?.beginTime !== '' && checkin?.endTime !== '' && checkout?.time !== ''
    const hasValidFees = fees?.mandatory !== '' || fees?.optional !== ''
    const hasValidPolicies = policies?.knowBeforeYouGo !== ''
    const hasValidComments = !!comments

    return hasValidCheckInCheckOut || hasValidFees || hasValidPolicies || hasValidComments
  }

  const isFacilitiesSectionAvailable = property.facilities?.length > 0

  const { sendEvent: sendClickBannerEvent } = useKafkaEvent({ page: pageId, eventName: 'click' })

  return (
    <div id="hotel-overview" className={cn(styles.layout)}>
      <BuilderModal />
      <Head pathname={localUrl.pathname} locale={locale} property={property} />
      <div className={styles.header}>
        <div ref={hotelOverviewRef} />
        <Header
          pageId="detail"
          theme={!isWide ? THEME_TERTIARY : undefined}
          backButton
          ancillariesJSON={ancillariesJSON}
        />
      </div>
      <Section gridArea="images" fullOnlyOnMobile>
        <Gallery
          key={property.id}
          onPriceClick={() => updateTabScroll(ContentSections.ROOMS)}
          onVisibilityChange={setIsGalleryVisible}
        />
      </Section>
      <div className={styles.navigationBar}>
        <NavigationBar
          offerCTA={
            <HotelDetailMainPrice
              handleClick={() => onTabClick(ContentSections.CTA)}
              showSubtitle={false}
            />
          }
        >
          <Tab
            onClick={() => onTabClick(ContentSections.OVERVIEW)}
            selected={sectionsActive.isOverViewTabSelected}
            ref={overviewTabRef}
            data-testid="overview-tab"
          >
            {intl.formatMessage({ id: 'global.overview' })}
          </Tab>

          <Tab
            onClick={() => onTabClick(ContentSections.ROOMS)}
            selected={sectionsActive.isRoomsTabSelected}
            ref={roomsTabRef}
            data-testid="rooms-tab"
          >
            {intl.formatMessage({ id: 'image.gallery-rooms' })}
          </Tab>

          <Tab
            onClick={() => onTabClick(ContentSections.REVIEWS)}
            selected={sectionsActive.isReviewsTabSelected}
            ref={reviewsTabRef}
            data-testid="reviews-tab"
          >
            {intl.formatMessage({ id: 'global.reviews' })}
          </Tab>

          <Tab
            onClick={() => onTabClick(ContentSections.DETAILS)}
            selected={sectionsActive.isDetailsTabSelected}
            ref={detailsTabRef}
            data-testid="details-tab"
          >
            {intl.formatMessage({ id: 'global.details' })}
          </Tab>

          {isFacilitiesSectionAvailable && (
            <Tab
              onClick={() => onTabClick(ContentSections.FACILITIES)}
              selected={sectionsActive.isFacilitiesTabSelected}
              ref={facilitiesTabRef}
              data-testid="facilities-tab"
            >
              {intl.formatMessage({ id: 'global.facilities' })}
            </Tab>
          )}
        </NavigationBar>
      </div>
      <div className={styles.title}>
        <HotelDetailTitle onRatingsClick={scrollToRatings} property={property} />
        <BackToResults propertyId={property?.id} {...location} />
      </div>

      <div className={styles.hotelDetailBanner}>
        <Banner.Billboard
          bannersJSON={bannersJSON}
          onClick={() => sendClickBannerEvent({ section: SECTIONS.BANNER_BILLBOARD })}
        />
      </div>

      <Separator fullWidth />
      <Section id="rooms" ref={roomsRef} hasBackgroundColor>
        <RoomSection
          handleScroll={handleScroll}
          isFetchingRooms={isFetchingRooms}
          property={property}
          searchDataUpdated={searchDataUpdated}
          source={source}
        />
      </Section>
      <Separator fullWidth />
      <Section ref={reviewsRef}>
        {loadedReviews && <Rating propertyId={property.id} ratings={property.ratings} />}
      </Section>
      <Separator />
      <Section ref={hotelDetailsRef}>
        <h2 className={styles.sectionTitle} data-testid="hotel-checkin">
          {intl.formatMessage({ id: 'global.description-accommodation' })}
        </h2>
        <Description description={property.description} />

        {isConditionsSectionAvailable() && (
          <>
            <h2
              className={cn(styles.sectionTitle, styles.checkInTitle)}
              data-testid="hotel-checkin"
            >
              {intl.formatMessage({ id: 'global.checkin-important-information' })}
            </h2>
            <Conditions {...property.conditions} onModal />
          </>
        )}
      </Section>
      <Section ref={hotelFacilitiesRef} hidden={!isFacilitiesSectionAvailable}>
        <h2 className={styles.sectionTitle} data-testid="hotel-facilities">
          {intl.formatMessage({ id: 'global.facilities' })}
        </h2>
        <FacilitiesV2 facilities={property.facilities} />
      </Section>
      <Separator hidden={!isFacilitiesSectionAvailable} />
      <Section>
        <MoreAccommodations city={property.city} resultsUrl={resultsURL} />
      </Section>
      <Advantages />
      <WidgetPlaceholder />
      <TrustPilotWidget />
      <SubscriptionBox source={SUBSCRIPTION_BOX_HOTEL_PAGE} />
      <Footer />
      {isMobile ? (
        <BottomSheet
          open={
            !roomsIntersection?.isIntersecting &&
            !isLoginOpened &&
            !isGalleryVisible &&
            !isRoomGalleryVisible
          }
          shouldScaleBackground={false}
          modal={false}
          noBodyStyles
          handleOnly
        >
          <BottomSheet.Content
            title={intl.formatMessage({ id: 'checkout.step-your-room' })}
            hiddenTitle
            data-testid="cta-choose-room"
            aria-describedby=""
          >
            <Button fullWidth size="m" onClick={onClickBottomSheetButton}>
              {intl.formatMessage({ id: 'checkout.step-your-room' })}
            </Button>
          </BottomSheet.Content>
        </BottomSheet>
      ) : null}
    </div>
  )
}
